(() => {
  if (customElements.get('sale-tide-feature')) return;

  const POSITION_CLASSES = [
    'is-active',
    'is-before-1',
    'is-before-2',
    'is-before-hidden',
    'is-after-1',
    'is-after-2',
    'is-after-hidden',
    'is-dormant',
  ];

  class SaleTideFeature extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.cards = Array.from(this.querySelectorAll('[data-sale-tide-card]'));
      this.dots = Array.from(this.querySelectorAll('[data-sale-tide-dot]'));
      this.deck = this.querySelector('[data-sale-tide-deck]');
      this.previousButton = this.querySelector('[data-sale-tide-prev]');
      this.nextButton = this.querySelector('[data-sale-tide-next]');
      this.pauseButton = this.querySelector('[data-sale-tide-pause]');
      this.pauseLabel = this.querySelector('[data-sale-tide-pause-label]');
      this.pauseIcon = this.querySelector('[data-sale-tide-pause-icon]');
      this.playIcon = this.querySelector('[data-sale-tide-play-icon]');
      this.liveRegion = this.querySelector('[data-sale-tide-live]');

      if (!this.cards.length || !this.deck) return;

      this.initialized = true;
      this.currentIndex = Math.max(0, this.cards.findIndex((card) => card.classList.contains('is-active')));
      this.autoplayEnabled = this.dataset.autoplay === 'true' && this.cards.length > 1;
      this.interval = Math.max(Number.parseInt(this.dataset.interval, 10) || 3000, 1600);
      this.style.setProperty('--sale-tide-flow-duration', `${this.interval}ms`);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.compactViewport = window.matchMedia('(max-width: 749px)');
      this.manualPaused = !this.autoplayEnabled || this.reducedMotion.matches;
      this.pointerPaused = false;
      this.focusPaused = false;
      this.interactionPaused = false;
      this.pagePaused = document.hidden;
      this.pointerStartX = null;
      this.didSwipe = false;
      this.autoplayTimer = null;
      this.interactionTimer = null;
      this.flowStartFrame = null;

      this.bindEvents();
      this.update(this.currentIndex, false);
      this.updatePauseControl();
      this.resumeAutoplay();
      this.classList.add('is-initialized');
    }

    disconnectedCallback() {
      this.pauseAutoplay();
      window.clearTimeout(this.interactionTimer);
    }

    bindEvents() {
      this.previousButton?.addEventListener('click', () => this.previous(true));
      this.nextButton?.addEventListener('click', () => this.next(true));
      this.pauseButton?.addEventListener('click', () => this.togglePause());

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const dotIndex = Number.parseInt(dot.dataset.saleTideDot, 10) || 0;
          const divisor = Math.max(this.dots.length - 1, 1);
          const targetIndex = Math.round((dotIndex / divisor) * (this.cards.length - 1));
          this.goTo(targetIndex, true);
        });
      });

      this.deck.addEventListener('keydown', (event) => this.onKeydown(event));
      this.deck.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.deck.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.deck.addEventListener('pointercancel', () => this.cancelPointer());
      this.deck.addEventListener('click', (event) => {
        if (this.didSwipe) {
          event.preventDefault();
          event.stopPropagation();
          this.didSwipe = false;
        }
        this.endInteraction();
      }, true);

      this.addEventListener('pointerenter', (event) => {
        if (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
        this.pointerPaused = true;
        this.pauseAutoplay();
      });
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
        this.pointerPaused = false;
        this.resumeAutoplay();
      });
      this.addEventListener('focusin', (event) => {
        if (event.target === this.pauseButton) return;
        this.focusPaused = true;
        this.pauseAutoplay();
      });
      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.focusPaused = false;
        this.resumeAutoplay();
      });

      document.addEventListener('visibilitychange', () => {
        this.pagePaused = document.hidden;
        if (this.pagePaused) {
          this.pauseAutoplay();
        } else {
          this.resumeAutoplay();
        }
      });

      const onMotionChange = (event) => {
        if (event.matches) {
          this.manualPaused = true;
          this.pauseAutoplay();
        }
        this.updatePauseControl();
      };

      if (typeof this.reducedMotion.addEventListener === 'function') {
        this.reducedMotion.addEventListener('change', onMotionChange);
      } else if (typeof this.reducedMotion.addListener === 'function') {
        this.reducedMotion.addListener(onMotionChange);
      }
    }

    onKeydown(event) {
      if (event.target.matches('input, select, textarea')) return;

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.previous(true);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.next(true);
      } else if (event.key === 'Home') {
        event.preventDefault();
        this.goTo(0, true);
      } else if (event.key === 'End') {
        event.preventDefault();
        this.goTo(this.cards.length - 1, true);
      }
    }

    onPointerDown(event) {
      if (!event.isPrimary) return;
      this.beginInteraction();
      this.pointerStartX = event.clientX;
      this.didSwipe = false;
      this.deck.setPointerCapture?.(event.pointerId);
    }

    onPointerUp(event) {
      if (this.pointerStartX === null || !event.isPrimary) return;

      const distance = event.clientX - this.pointerStartX;
      this.pointerStartX = null;
      const stepCount = this.getSwipeStepCount(distance);
      if (!stepCount) {
        this.endInteraction();
        return;
      }

      this.didSwipe = true;
      const direction = distance < 0 ? 1 : -1;
      this.goTo(this.currentIndex + (direction * stepCount), true);
      this.endInteraction();
    }

    getSwipeStepCount(distance) {
      const absoluteDistance = Math.abs(distance);
      if (absoluteDistance < 42) return 0;

      const deckWidth = Math.max(this.deck?.clientWidth || 390, 280);
      const distancePerProduct = Math.max(deckWidth * 0.16, 56);
      return Math.min(4, Math.max(1, Math.round(absoluteDistance / distancePerProduct)));
    }

    cancelPointer() {
      this.pointerStartX = null;
      this.didSwipe = false;
      this.endInteraction();
    }

    previous(isManual = false) {
      this.goTo(this.currentIndex - 1, isManual);
    }

    next(isManual = false) {
      this.goTo(this.currentIndex + 1, isManual);
    }

    goTo(index, isManual = false) {
      this.update(index, true);
      if (isManual) {
        this.beginInteraction();
        this.endInteraction();
      }
    }

    update(index, announce = true) {
      const count = this.cards.length;
      this.currentIndex = ((index % count) + count) % count;

      this.cards.forEach((card, cardIndex) => {
        POSITION_CLASSES.forEach((className) => card.classList.remove(className));

        let delta = (cardIndex - this.currentIndex + count) % count;
        if (delta > count / 2) delta -= count;

        if (delta === 0) card.classList.add('is-active');
        else if (delta === -1) card.classList.add('is-before-1');
        else if (delta === -2) card.classList.add('is-before-2');
        else if (delta === -3) card.classList.add('is-before-hidden');
        else if (delta === 1) card.classList.add('is-after-1');
        else if (delta === 2) card.classList.add('is-after-2');
        else if (delta === 3) card.classList.add('is-after-hidden');
        else card.classList.add('is-dormant');

        this.updateCardImage(card, Math.abs(delta) <= 3);

        const isActive = delta === 0;
        card.setAttribute('aria-hidden', String(!isActive));
        card.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          element.tabIndex = isActive ? 0 : -1;
        });
      });

      const activeDot = this.cards.length <= 1 || this.dots.length <= 1
        ? 0
        : Math.round((this.currentIndex / (this.cards.length - 1)) * (this.dots.length - 1));

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === activeDot;
        dot.classList.toggle('is-active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (announce && this.liveRegion) {
        const title = this.cards[this.currentIndex].dataset.saleTideTitle || 'Sale product';
        this.liveRegion.textContent = `${title}. Deal ${this.currentIndex + 1} of ${this.cards.length}.`;
      }
    }

    updateCardImage(card, shouldLoad) {
      const image = card.querySelector?.('[data-sale-tide-image]');
      if (!image) return;

      const currentSrc = image.getAttribute('src');
      const currentSrcset = image.getAttribute('srcset');
      const currentSizes = image.getAttribute('sizes');

      if (!image.dataset.saleTideSrc && currentSrc) image.dataset.saleTideSrc = currentSrc;
      if (!image.dataset.saleTideSrcset && currentSrcset) image.dataset.saleTideSrcset = currentSrcset;
      if (!image.dataset.saleTideSizes && currentSizes) image.dataset.saleTideSizes = currentSizes;

      if (shouldLoad) {
        if (image.dataset.saleTideSizes) image.setAttribute('sizes', image.dataset.saleTideSizes);
        if (image.dataset.saleTideSrcset) image.setAttribute('srcset', image.dataset.saleTideSrcset);
        const source = this.compactViewport?.matches && image.dataset.saleTideMobileSrc
          ? image.dataset.saleTideMobileSrc
          : image.dataset.saleTideSrc;
        if (source) image.setAttribute('src', source);
        return;
      }

      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.removeAttribute('src');
    }

    togglePause() {
      this.manualPaused = !this.manualPaused;
      this.updatePauseControl();

      if (this.manualPaused) {
        this.pauseAutoplay();
      } else {
        this.resumeAutoplay();
      }
    }

    updatePauseControl() {
      if (!this.pauseButton) return;

      const isPaused = this.manualPaused || !this.autoplayEnabled;
      this.pauseButton.setAttribute('aria-pressed', String(isPaused));
      this.pauseButton.disabled = !this.autoplayEnabled;
      if (this.pauseLabel) this.pauseLabel.textContent = isPaused ? 'Play' : 'Pause';
      if (this.pauseIcon) this.pauseIcon.hidden = isPaused;
      if (this.playIcon) this.playIcon.hidden = !isPaused;
    }

    beginInteraction() {
      window.clearTimeout(this.interactionTimer);
      this.interactionTimer = null;
      this.interactionPaused = true;
      this.pauseAutoplay();
    }

    endInteraction(delay = 900) {
      window.clearTimeout(this.interactionTimer);
      this.interactionTimer = window.setTimeout(() => {
        this.interactionTimer = null;
        this.interactionPaused = false;
        this.resumeAutoplay();
      }, delay);
    }

    pauseAutoplay() {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
      if (this.flowStartFrame) {
        window.cancelAnimationFrame(this.flowStartFrame);
        this.flowStartFrame = null;
      }
    }

    resumeAutoplay() {
      if (
        !this.autoplayEnabled
        || this.manualPaused
        || this.pointerPaused
        || this.focusPaused
        || this.interactionPaused
        || this.pagePaused
        || this.reducedMotion.matches
        || this.autoplayTimer
        || this.flowStartFrame
      ) return;

      this.flowStartFrame = window.requestAnimationFrame(() => {
        this.flowStartFrame = null;
        if (
          this.manualPaused
          || this.pointerPaused
          || this.focusPaused
          || this.interactionPaused
          || this.pagePaused
          || this.reducedMotion.matches
        ) return;

        this.next(false);
        this.autoplayTimer = window.setInterval(() => this.next(false), this.interval);
      });
    }
  }

  customElements.define('sale-tide-feature', SaleTideFeature);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('sale-tide-feature').forEach((feature) => feature.connectedCallback());
  });
})();
